import React, { useState, useEffect, useRef } from 'react';
import '../../styles/Dashboard.css';
import { InputText } from "primereact/inputtext";
import { useNavigate } from 'react-router-dom';
import routesConfig from '../../config/routes.json';

const DashboardHeader = ({
                             selectedSidebarItem,
                             headerItems,
                             hasNotifications,
                             userAvatar,
                             toggleNotifications,
                             navigateToProfile,
                             isOnProfilePage,
                             userCity,
                             partnerInfo,
                             toggleSidebar
                         }) => {
    const [activeMenuIndex, setActiveMenuIndex] = useState(0);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1024);
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const menuContainerRef = useRef(null);
    const searchContainerRef = useRef(null);
    const isHomeView = selectedSidebarItem === 'Home';
    const navigate = useNavigate();

    const routes = routesConfig.routes;

    useEffect(() => {
        const handleResize = () => setIsMobileView(window.innerWidth < 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const activeIndex = headerItems.findIndex(item => item.className === 'active');
        if (activeIndex !== -1) {
            setActiveMenuIndex(activeIndex);
        }
    }, [headerItems]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setShowSuggestions(false);
                setSelectedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearch = (e) => {
        const query = e.target.value.toLowerCase();
        setSearchQuery(e.target.value);
        
        if (query.length > 0) {
            const matches = Object.keys(routes).filter(key => 
                key.toLowerCase().includes(query)
            );
            setSuggestions(matches);
            setShowSuggestions(true);
            setSelectedIndex(-1);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
            setSelectedIndex(-1);
        }
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => 
                    prev < suggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
                    handleSuggestionClick(suggestions[selectedIndex]);
                }
                break;
            case 'Escape':
                setShowSuggestions(false);
                setSelectedIndex(-1);
                break;
            default:
                break;
        }
    };

    const handleSuggestionClick = (suggestion) => {
        navigate(routes[suggestion]);
        setSearchQuery('');
        setSuggestions([]);
        setShowSuggestions(false);
        setSelectedIndex(-1);
    };

    const nextMenuItem = () => {
        const nextIndex = (activeMenuIndex + 1) % headerItems.length;
        setActiveMenuIndex(nextIndex);
        if (headerItems[nextIndex]?.command) {
            headerItems[nextIndex].command();
        }
    };

    const previousMenuItem = () => {
        const prevIndex = (activeMenuIndex - 1 + headerItems.length) % headerItems.length;
        setActiveMenuIndex(prevIndex);
        if (headerItems[prevIndex]?.command) {
            headerItems[prevIndex].command();
        }
    };

    const renderBurgerMenu = () => (
        <button className="burger-menu-button" onClick={toggleSidebar}>
            <i className="pi pi-bars"></i>
        </button>
    );

    const renderSearchBar = () => (
        <div className="search-box-header" ref={searchContainerRef}>
            <i className="pi pi-search search-icon"></i>
            <InputText
                value={searchQuery}
                onChange={handleSearch}
                onKeyDown={handleKeyDown}
                placeholder="Search..."
                className="main-search"
            />
            {showSuggestions && suggestions.length > 0 && (
                <div className="search-suggestions">
                    {suggestions.map((suggestion, index) => (
                        <div
                            key={index}
                            className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
                            onClick={() => handleSuggestionClick(suggestion)}
                        >
                            {suggestion}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    const renderNavigationMenu = () => (
        <div
            className="header-menu-custom"
            style={isHomeView ? { display: 'none' } : {}}
        >
            <div className="header-menu-items-container" ref={menuContainerRef}>
                {isMobileView && (
                    <div className="header-menu-arrow left" onClick={(e) => {
                        e.stopPropagation();
                        previousMenuItem();
                    }}>
                        <i className="pi pi-chevron-left"></i>
                    </div>
                )}
                {(isMobileView
                    ? [headerItems[activeMenuIndex]]
                    : headerItems
                ).map((item, index) => {
                    if (!item) return null;
                    return (
                        <div
                            key={index}
                            className={`header-menu-item ${item.className || ''} ${activeMenuIndex === index ? 'visible' : ''}`}
                            onClick={item.command}
                        >
                            {item.label}
                        </div>
                    );
                })}
                {isMobileView && (
                    <div className="header-menu-arrow right" onClick={(e) => {
                        e.stopPropagation();
                        nextMenuItem();
                    }}>
                        <i className="pi pi-chevron-right"></i>
                    </div>
                )}
            </div>
        </div>
    );

    const renderUserAvatar = () => (
        <div className="header-actions">
            {userAvatar ? (
                <img
                    src={userAvatar}
                    alt="User Avatar"
                    className="header-user-avatar"
                    onClick={navigateToProfile}
                />
            ) : (
                <i
                    className="pi pi-user p-mr-3"
                    style={{ fontSize: '1.2rem' }}
                    onClick={navigateToProfile}
                />
            )}
        </div>
    );

    return (
        <header
            className={`dashboard-header ${isHomeView ? 'home-active absolute-header' : 'non-home-header fixed-header'} ${isOnProfilePage ? 'profile-page' : ''}`}
        >
            {!isOnProfilePage && (
                <div className="header-content">
                    {isMobileView && renderBurgerMenu()}

                    {isHomeView && renderSearchBar()}

                    {(isMobileView || (!isHomeView && !isOnProfilePage)) && (
                        <>
                            <div className="header-menu-spacer"></div>
                            {renderNavigationMenu()}
                        </>
                    )}

                    {renderUserAvatar()}
                </div>
            )}
        </header>
    );
};

export default React.memo(DashboardHeader);